import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { Popover, TextField, Box } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { selectStyle } from "./BudgetScopeFilters";

function toIso(d) {
  if (!d) return "";
  return dayjs(d).format("YYYY-MM-DD");
}

function formatRangeLabel(start, end) {
  if (!start || !end) return "Select dates";
  const s = dayjs(start);
  const e = dayjs(end);
  if (s.isSame(e, "day")) return s.format("MMM D, YYYY");
  if (s.isSame(e, "year")) return `${s.format("MMM D")} – ${e.format("MMM D, YYYY")}`;
  return `${s.format("MMM D, YYYY")} – ${e.format("MMM D, YYYY")}`;
}

export default function BudgetCustomRangePicker({ start, end, onChange }) {
  const [anchor, setAnchor] = useState(null);
  const [pickStep, setPickStep] = useState("start");

  const startDay = start ? dayjs(start) : null;
  const endDay = end ? dayjs(end) : null;

  const label = useMemo(() => formatRangeLabel(start, end), [start, end]);

  const openPicker = (e) => {
    setPickStep("start");
    setAnchor(e.currentTarget);
  };

  const closePicker = () => setAnchor(null);

  const handlePick = (value) => {
    if (!value || !value.isValid()) return;
    const iso = toIso(value);

    if (pickStep === "start") {
      onChange({ start: iso, end: end && iso > end ? iso : end });
      setPickStep("end");
      return;
    }

    let nextStart = start;
    let nextEnd = iso;
    if (start && iso < start) {
      nextStart = iso;
      nextEnd = start;
    }
    onChange({ start: nextStart, end: nextEnd });
    closePicker();
  };

  const calendarValue = pickStep === "start" ? startDay : endDay || startDay;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TextField
        size="small"
        value={label}
        onClick={openPicker}
        inputProps={{ readOnly: true }}
        sx={{
          minWidth: 220,
          "& .MuiOutlinedInput-root": {
            background: selectStyle.background,
            borderRadius: selectStyle.borderRadius,
            fontSize: selectStyle.fontSize,
            fontWeight: selectStyle.fontWeight,
            cursor: "pointer",
            "& fieldset": { borderColor: selectStyle.border },
          },
          "& .MuiInputBase-input": { cursor: "pointer", py: "7px" },
        }}
      />
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={closePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 1.5, minWidth: 300 }}>
          <Box sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#475569", mb: 1 }}>
            {pickStep === "start" ? "Select start date" : "Select end date"}
          </Box>
          <DateCalendar value={calendarValue} onChange={handlePick} />
        </Box>
      </Popover>
    </LocalizationProvider>
  );
}
